import { useContext } from "react";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { buildClasses } from "../utils/buildClasses";
import styles from "./DollarAmount.module.scss";

/** What an amount of zero shows, without a currency symbol or sign. */
const ZERO_AMOUNT = "—";
/** What the digits become when the user has asked to hide dollar amounts. */
const HIDDEN_AMOUNT = "##";

export interface DollarAmountFormat {
  /** Defaults to the user's default currency. */
  currencyCode?: string;
  /** Use 2 decimal places instead of 0. */
  includeDecimals?: boolean;
  showPlus?: boolean;
  showMinus?: boolean;
  showDollarSign?: boolean;
  /** A short form for chart axes, like $1.2K. Zero shows as $0, the baseline. */
  compact?: boolean;
}

/**
 * Every rule for showing an amount: hiding it, zero, sign, currency symbol,
 * and decimals. The sign comes back separately so DollarAmount can space it.
 */
export function formatDollarAmountParts(
  n: number,
  {
    currencyCode,
    includeDecimals = false,
    showPlus = false,
    showMinus = false,
    showDollarSign = false,
    compact = false,
  }: DollarAmountFormat,
  prefs: { hide: boolean; defaultCurrency: string },
): { sign: string; text: string } {
  if (n === 0 && !compact) return { sign: "", text: ZERO_AMOUNT };

  const numDecimals = includeDecimals ? 2 : 0;
  const digits = new Intl.NumberFormat("en-US", {
    signDisplay: "never",
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: compact ? 0 : numDecimals,
    maximumFractionDigits: compact ? 1 : numDecimals,
  }).format(n);
  // Checked on the formatted digits rather than with round(), since
  // Intl.NumberFormat doesn't round quite the same way.
  const roundsToZero = !/[1-9]/.test(digits);

  let sign = "";
  if (!roundsToZero && n > 0 && showPlus) sign = "+";
  if (!roundsToZero && n < 0 && showMinus) sign = "-";
  const symbol = showDollarSign
    ? getCurrencySymbol("en-US", currencyCode ?? prefs.defaultCurrency)
    : "";
  return { sign, text: `${symbol}${prefs.hide ? HIDDEN_AMOUNT : digits}` };
}

function useDollarAmountPrefs(): { hide: boolean; defaultCurrency: string } {
  const userPrefs = useContext(PreferencesContext);
  return {
    hide: userPrefs?.web?.hide_dollar_amounts ?? false,
    defaultCurrency: userPrefs?.web?.default_currency ?? "USD",
  };
}

/**
 * Formats amounts as plain text, by the same rules as DollarAmount, for places
 * that can't render it: SVG chart text, axis labels, and sentences.
 */
export function useFormatDollarAmount(): (
  n: number,
  format?: DollarAmountFormat,
) => string {
  const prefs = useDollarAmountPrefs();
  return (n, format = {}) => {
    const { sign, text } = formatDollarAmountParts(n, format, prefs);
    return `${sign}${text}`;
  };
}

export function getCurrencySymbol(
  locale: string,
  currencyCode: string,
): string {
  try {
    // Format 0 as currency and remove digits to isolate the currency symbol/prefix
    return (0)
      .toLocaleString(locale, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, "")
      .trim();
  } catch (e) {
    return "$";
  }
}

/** Whether the user has asked for dollar amounts to be hidden. */
export function useHideDollarAmounts(): boolean {
  const userPrefs = useContext(PreferencesContext);
  return userPrefs?.web?.hide_dollar_amounts ?? false;
}

const NUMBER_PART_TYPES = new Set<Intl.NumberFormatPartTypes>([
  "integer",
  "group",
  "decimal",
  "fraction",
  "compact",
]);

/**
 * Formats an amount with a currency formatter, for charts that draw amounts
 * as plain text rather than with DollarAmount. When amounts are hidden, the
 * digits become "##" like DollarAmount's, keeping the symbol and sign.
 */
export function formatCurrency(
  format: Intl.NumberFormat,
  amount: number,
  hide: boolean,
): string {
  if (!hide) return format.format(amount);
  let masked = false;
  return format
    .formatToParts(amount)
    .map((part) => {
      if (!NUMBER_PART_TYPES.has(part.type)) return part.value;
      if (masked) return "";
      masked = true;
      return "##";
    })
    .join("");
}

export function DollarAmount({
  n,
  monospaceFont = true,
  align = "right",
  size = "md",
  ...format
}: {
  n: number;
  monospaceFont?: boolean;
  align?: "left" | "center" | "right" | "span";
  size?: "xs" | "sm" | "md" | "lg";
} & DollarAmountFormat): JSX.Element {
  const { sign, text } = formatDollarAmountParts(
    n,
    format,
    useDollarAmountPrefs(),
  );

  // On the sign and the digits alike, as a global style gives spans a font
  // size of their own rather than inheriting it.
  const sizeClass = buildClasses({
    switch: size,
    cases: new Map([
      ["xs", "text-xs"],
      ["sm", "text-sm"],
      ["md", "text-md"],
      ["lg", "text-lg"],
    ]),
  });
  const signContent = sign ? (
    <span className={buildClasses(styles.sign, sizeClass)}>{sign}</span>
  ) : null;
  const textContent = (
    <span className={buildClasses("whitespace-nowrap", sizeClass)}>{text}</span>
  );

  if (align === "span") {
    return (
      <span className={buildClasses({ if: monospaceFont, then: "font-mono" })}>
        {signContent}
        {textContent}
      </span>
    );
  }
  return (
    <div
      className={
        buildClasses({ if: monospaceFont, then: "font-mono" }) +
        ` ${styles.amountContainer}  ${
          align === "left" ? styles.alignLeft : ""
        } ${align === "center" ? styles.alignCenter : ""}`
      }
    >
      {signContent}
      {textContent}
    </div>
  );
}
